import Cookies from 'js-cookie'

const TOKEN_COOKIE = '@Habitar:token'
const COMPANY_ID_COOKIE = '@Habitar:companyId'
const LEGACY_COMPANY_COOKIE = '@Habitar:companyUid'
const PERMISSIONS_STORAGE_PREFIX = '@Habitar:permissions'
const USER_STORAGE_KEY = '@Habitar:user'
const TAB_COMPANY_KEY = '@Habitar:tab-company'
export const ASSISTANT_STORAGE_PREFIX = '@Habitar:assistant'

const ACCESS_TOKEN_PREFIX = 'hbt_'
const COMPANY_ID_PREFIX = 'cmp_'

export interface SessionUser {
  id: string
  name: string
  email: string
  image?: string
  isActive?: boolean
}

export const EMPTY_USER: SessionUser = { id: '', name: '', email: '' }

export const isAccessToken = (value?: string | null): value is string => typeof value === 'string' && value.startsWith(ACCESS_TOKEN_PREFIX)

export const isCompanyId = (value?: string | null): value is string => typeof value === 'string' && value.startsWith(COMPANY_ID_PREFIX)

export function cookieExpiration(expiresAt?: string | null): Date | undefined {
  if (!expiresAt) return undefined
  const date = new Date(expiresAt)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export function parseStoredUser(raw: string | null | undefined): SessionUser | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<SessionUser> | null
    if (!parsed || typeof parsed.id !== 'string' || parsed.id === '') return null
    return { ...EMPTY_USER, ...parsed } as SessionUser
  } catch {
    return null
  }
}

let endedToken: string | null = null

export function getAccessToken(): string | undefined {
  const token = Cookies.get(TOKEN_COOKIE)
  return isAccessToken(token) && token !== endedToken ? token : undefined
}

function cookieOptions(expires?: Date): Cookies.CookieAttributes {
  const secure = typeof location !== 'undefined' && location.protocol === 'https:'
  return { sameSite: 'lax', ...(secure ? { secure } : {}), ...(expires ? { expires } : {}) }
}

export function setAccessToken(accessToken: string, expiresAt?: string) {
  endedToken = null
  Cookies.set(TOKEN_COOKIE, accessToken, cookieOptions(cookieExpiration(expiresAt)))
}

function readTabCompany(): string | null {
  try {
    return sessionStorage.getItem(TAB_COMPANY_KEY)
  } catch {
    return null
  }
}

function writeTabCompany(companyId: string | null) {
  try {
    if (companyId) sessionStorage.setItem(TAB_COMPANY_KEY, companyId)
    else sessionStorage.removeItem(TAB_COMPANY_KEY)
  } catch {
    return
  }
}

export function getCompanyId(): string | undefined {
  const tabCompany = readTabCompany()
  if (isCompanyId(tabCompany)) return tabCompany
  const companyId = Cookies.get(COMPANY_ID_COOKIE)
  return isCompanyId(companyId) ? companyId : undefined
}

export function setCompanyId(companyId: string) {
  Cookies.set(COMPANY_ID_COOKIE, companyId, cookieOptions())
  writeTabCompany(companyId)
}

export function clearCompanyId() {
  Cookies.remove(COMPANY_ID_COOKIE)
  Cookies.remove(LEGACY_COMPANY_COOKIE)
  writeTabCompany(null)
}

function clearStorageWithPrefix(prefix: string) {
  if (typeof window === 'undefined') return
  Object.keys(localStorage)
    .filter(key => key.startsWith(prefix))
    .forEach(key => localStorage.removeItem(key))
}

export function clearPermissionsCache() {
  clearStorageWithPrefix(PERMISSIONS_STORAGE_PREFIX)
}

export function readStoredUser(): SessionUser {
  if (typeof window === 'undefined') return EMPTY_USER
  const user = parseStoredUser(localStorage.getItem(USER_STORAGE_KEY))
  if (!user) localStorage.removeItem(USER_STORAGE_KEY)
  return user ?? EMPTY_USER
}

export function storeUser(user: SessionUser) {
  if (typeof window === 'undefined') return
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
}

export function clearSession() {
  Cookies.remove(TOKEN_COOKIE)
  clearCompanyId()
  clearPermissionsCache()
  clearStorageWithPrefix(ASSISTANT_STORAGE_PREFIX)
  if (typeof window !== 'undefined') localStorage.removeItem(USER_STORAGE_KEY)
}

const sessionEndedListeners = new Set<() => void>()

export function onSessionEnded(listener: () => void): () => void {
  sessionEndedListeners.add(listener)
  return () => {
    sessionEndedListeners.delete(listener)
  }
}

export function endSession() {
  const token = getAccessToken()
  if (token) endedToken = token
  const hadSession = token !== undefined
  clearSession()
  if (hadSession) for (const listener of [...sessionEndedListeners]) listener()
}

export function startSession(session: { accessToken: string; expiresAt?: string; user: SessionUser }) {
  clearCompanyId()
  clearPermissionsCache()
  setAccessToken(session.accessToken, session.expiresAt)
  storeUser(session.user)
}

export function purgeLegacySession() {
  const token = Cookies.get(TOKEN_COOKIE)
  const legacyCompany = Cookies.get(LEGACY_COMPANY_COOKIE)
  const companyId = Cookies.get(COMPANY_ID_COOKIE)

  const hasLegacyToken = token !== undefined && !isAccessToken(token)
  const hasLegacyCompany = legacyCompany !== undefined || (companyId !== undefined && !isCompanyId(companyId))

  if (!hasLegacyToken && !hasLegacyCompany) return

  if (hasLegacyToken) Cookies.remove(TOKEN_COOKIE)
  clearCompanyId()
  clearPermissionsCache()
}
